import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { toast } from '@/hooks/useToast';
import { useEncounterMutation, useErMeta } from '../hooks';
import { addCharge, removeCharge } from '../api';
import { Section, errorInfo, timeLabel } from '../ui';
import type { Workspace } from '../types';

const TYPES = ['consultation', 'procedure', 'consumable', 'investigation', 'monitoring', 'ambulance', 'other'];

/**
 * ED charges ACCRUE (D8) — nothing here is ever a precondition for care.
 * They settle at departure: rolled into the IP bill on admission (one bill,
 * settled at IP discharge), or billed as an ER bill otherwise, collected from
 * the Billing module like any other bill.
 */
export default function ChargesSection({ ws }: { ws: Workspace }) {
  const id = ws.encounter.id;
  const meta = useErMeta();
  const open = ws.encounter.status !== 'departed';
  const [f, setF] = useState({ charge_type: 'consultation', description: '', quantity: 1, rate: '' as number | '', gst_percent: 0, payer_scheme_code: '' });
  const add = useEncounterMutation(id, (input: Parameters<typeof addCharge>[1]) => addCharge(id, input));
  const del = useEncounterMutation(id, (cid: number) => removeCharge(cid));
  const total = ws.charges.reduce((s, c) => s + Number(c.amount), 0);

  return (
    <Section title="ED charges" actions={<span className="text-[13px] font-semibold">Total ₹{total.toFixed(2)}</span>}>
      <div className="mb-3 rounded-lg bg-info-soft px-3 py-2 text-[12px] text-info-ink">
        Charges never gate treatment. {ws.admission ? 'This patient was admitted — charges roll into the IP bill.' : 'They are billed when the patient leaves the ED, or rolled into the IP bill on admission.'}
        {ws.bill && <> ER bill <b>{ws.bill.bill_number}</b> · ₹{Number(ws.bill.total_amount).toFixed(2)} · {ws.bill.payment_status}.</>}
      </div>
      {open && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-6">
          <FormField label="Type">
            <NativeSelect value={f.charge_type} onChange={(e) => setF({ ...f, charge_type: e.target.value })}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Description" className="col-span-2"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></FormField>
          <FormField label="Qty"><Input type="number" min={1} value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) || 1 })} /></FormField>
          <FormField label="Rate ₹"><Input type="number" min={0} step="0.01" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value === '' ? '' : Number(e.target.value) })} /></FormField>
          <FormField label="Payer">
            <NativeSelect value={f.payer_scheme_code} onChange={(e) => setF({ ...f, payer_scheme_code: e.target.value })}>
              <option value="">Encounter default</option>
              {(meta.data?.payer_schemes ?? []).map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </NativeSelect>
          </FormField>
          <div className="col-span-2 flex items-end justify-end sm:col-span-6">
            <Button size="sm" loading={add.isPending} disabled={!f.description.trim() || f.rate === ''}
              onClick={() => add.mutateAsync({
                charge_type: f.charge_type, description: f.description, quantity: f.quantity, rate: Number(f.rate),
                gst_percent: f.gst_percent, payer_scheme_code: f.payer_scheme_code || undefined,
              }).then(() => setF({ ...f, description: '', quantity: 1, rate: '' })).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }))}>Add charge</Button>
          </div>
        </div>
      )}
      {ws.charges.length === 0 ? <div className="text-[13px] text-ink-muted">No charges.</div> : (
        <table className="w-full text-[12px]">
          <thead><tr className="border-b border-border text-left text-ink-muted"><th className="py-1">Item</th><th>Qty</th><th>Rate</th><th>Amount</th><th>Payer</th><th>Status</th><th /></tr></thead>
          <tbody>
            {ws.charges.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-b-0">
                <td className="py-1.5">{c.description} <span className="text-ink-muted">· {c.charge_type} · {timeLabel(c.created_at)}</span></td>
                <td>{c.quantity}</td><td>₹{Number(c.rate).toFixed(2)}</td><td>₹{Number(c.amount).toFixed(2)}</td>
                <td>{c.payer_scheme_code ?? '—'}</td>
                <td>{c.transferred_to_ip_charge_id ? <Badge variant="info">IP bill</Badge> : c.bill_id ? <Badge variant="success">billed</Badge> : <Badge variant="neutral">accrued</Badge>}</td>
                <td className="text-right">
                  {open && !c.bill_id && !c.transferred_to_ip_charge_id && (
                    <Button size="sm" variant="link" onClick={() => del.mutateAsync(c.id).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }))}>Remove</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}
